import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ============================================
// Types
// ============================================

export interface NoteAuthor {
  _id: string;
  name: string;
  avatar: string;
}

export interface Note {
  _id: string;
  title: string;
  description: string;
  subject: string;
  /** Eski kayıtlar */
  grade?: string;
  university: string;
  universityYear: string;
  department?: string;
  tags: string[];
  fileUrl: string;
  fileName: string;
  fileSize: number;
  author: NoteAuthor;
  downloads: number;
  rating: number;
  ratingCount: number;
  isApproved: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  averageRating: number;
}

export interface Pagination {
  current: number;
  pages: number;
  total: number;
  limit: number;
}

export interface NotesParams {
  page?: number;
  limit?: number;
  subject?: string;
  universityYear?: string;
  university?: string;
  department?: string;
  minDownloads?: number;
  search?: string;
  sort?: 'newest' | 'oldest' | 'rating' | 'downloads';
  /** false: istek atılmaz (ör. arama sayfasında boş sorgu) */
  enabled?: boolean;
}

// ============================================
// useNotes - Fetch notes from API
// ============================================

export function useNotes(params: NotesParams = {}) {
  const enabled = params.enabled !== false;
  const [notes, setNotes] = useState<Note[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(() => enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getNotes(params);
      if (result.success) {
        setNotes(result.data.notes || []);
        setPagination(result.data.pagination || null);
      }
    } catch (err: any) {
      console.error('Failed to fetch notes:', err);
      setError(err.message || 'Notlar yüklenirken hata oluştu');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.limit, params.subject, params.universityYear, params.university, params.department, params.minDownloads, params.search, params.sort, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setNotes([]);
      setPagination(null);
      setError(null);
      return;
    }
    fetchNotes();
  }, [fetchNotes, enabled]);

  return { notes, pagination, loading, error, refetch: fetchNotes };
}

/** Popüler (indirme) notlar; boşsa en yenilere düşer */
export function usePopularNotes(limit = 6) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const popular = await api.getNotes({ sort: 'downloads', minDownloads: 1, limit });
        const list = popular.success ? popular.data?.notes || [] : [];
        if (!cancelled && list.length > 0) {
          setNotes(list);
          setIsFallback(false);
        } else {
          const newest = await api.getNotes({ sort: 'newest', limit });
          if (!cancelled) {
            setNotes(newest.success ? newest.data?.notes || [] : []);
            setIsFallback(true);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Notlar yüklenirken hata oluştu');
          setNotes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { notes, loading, error, isFallback };
}

// ============================================
// useSubjectCounts - Fetch note counts per subject
// ============================================

const SUBJECTS = [
  'matematik', 'fizik', 'kimya', 'biyoloji', 'turkce',
  'tarih', 'cografya', 'felsefe', 'edebiyat',
] as const;

export type SubjectKey = typeof SUBJECTS[number];

export function useSubjectCounts() {
  const [counts, setCounts] = useState<Record<SubjectKey, number>>(
    Object.fromEntries(SUBJECTS.map(s => [s, 0])) as Record<SubjectKey, number>
  );
  const [totalNotes, setTotalNotes] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchCounts() {
      setLoading(true);
      try {
        const promises = SUBJECTS.map(subject =>
          api.getNotes({ subject, limit: 1 }).catch(() => null)
        );
        const results = await Promise.all(promises);

        if (cancelled) return;

        const newCounts = { ...counts };
        let total = 0;

        results.forEach((result, i) => {
          if (result?.success && result.data?.pagination) {
            newCounts[SUBJECTS[i]] = result.data.pagination.total;
            total += result.data.pagination.total;
          }
        });

        setCounts(newCounts);
        setTotalNotes(total);
      } catch (err) {
        console.error('Failed to fetch subject counts:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCounts();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { counts, totalNotes, loading };
}

// ============================================
// useStats - Aggregate stats for hero section
// ============================================

export function useStats() {
  const [stats, setStats] = useState({
    totalNotes: 0,
    totalDownloads: 0,
    totalUsers: 0,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchStats() {
      try {
        const result = await api.getNotesStatsSummary();
        if (cancelled) return;

        if (result.success && result.data) {
          setStats({
            totalNotes: result.data.totalNotes ?? 0,
            totalDownloads: result.data.totalDownloads ?? 0,
            totalUsers: result.data.totalUsers ?? 0,
            loaded: true,
          });
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        if (!cancelled) {
          setStats(prev => ({ ...prev, loaded: true }));
        }
      }
    }

    fetchStats();
    return () => { cancelled = true; };
  }, []);

  return stats;
}

export interface SiteFooterLink {
  label: string;
  href: string;
}

export interface CommunityLink {
  label: string;
  href: string;
  description?: string;
}

export interface SiteSettings {
  universities: string[];
  departments: string[];
  footerSubjects: SiteFooterLink[];
  footerResources: SiteFooterLink[];
  footerSupport: SiteFooterLink[];
  whatsappLinks: CommunityLink[];
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSiteSettings();
      if (res.success && res.data?.settings) {
        setSettings({
          universities: res.data.settings.universities || [],
          departments: res.data.settings.departments || [],
          footerSubjects: res.data.settings.footerSubjects || [],
          footerResources: res.data.settings.footerResources || [],
          footerSupport: res.data.settings.footerSupport || [],
          whatsappLinks: res.data.settings.whatsappLinks || [],
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { settings, loading, refetch };
}

// ============================================
// useDownloadNote - Handle note download
// ============================================

export function useDownloadNote() {
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = useCallback(async (noteId: string, fileName: string, fileUrl?: string) => {
    setDownloading(noteId);
    try {
      await api.downloadNoteFile(noteId, fileName, fileUrl || '');
    } catch (err) {
      console.error('Download failed:', err);
      throw err;
    } finally {
      setDownloading(null);
    }
  }, []);

  return { download, downloading };
}

// ============================================
// useForumStats - Forum summary counts
// ============================================

export interface ForumStats {
  totalThreads: number;
  totalReplies: number;
}

export function useForumStats() {
  const [stats, setStats] = useState<ForumStats | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getForumStats();
      if (res.success && res.data) {
        setStats(res.data);
      }
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { stats, loading, refetch };
}

// ============================================
// useUserStats - Logged-in user points & level
// ============================================

export interface UserStats {
  points: number;
  level: number;
  notesApproved: number;
}

export function useUserStats(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false;
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(() => enabled);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getUserStats();
      if (res.success && res.data) {
        setStats({
          points: res.data.points ?? 0,
          level: res.data.level ?? 1,
          notesApproved: res.data.notesApproved ?? 0,
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'İstatistikler yüklenemedi';
      setError(msg);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setStats(null);
      return;
    }
    refetch();
  }, [enabled, refetch]);

  return { stats, loading, error, refetch };
}

// ============================================
// useMyNotes - Current user's uploaded notes
// ============================================

export type MyNoteStatus = 'pending' | 'approved';

export interface MyNote extends Note {
  status: MyNoteStatus;
}

export interface MyNotesParams {
  page?: number;
  limit?: number;
  status?: 'all' | 'pending' | 'approved';
  enabled?: boolean;
}

export function useMyNotes(params: MyNotesParams = {}) {
  const enabled = params.enabled !== false;
  const [notes, setNotes] = useState<MyNote[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(() => enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchMyNotes = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getMyNotes({
        page: params.page,
        limit: params.limit,
        status: params.status,
      });
      if (result.success) {
        setNotes(result.data.notes || []);
        setPagination(result.data.pagination || null);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Notlarınız yüklenemedi';
      setError(msg);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.limit, params.status, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setNotes([]);
      setPagination(null);
      return;
    }
    fetchMyNotes();
  }, [fetchMyNotes, enabled]);

  return { notes, pagination, loading, error, refetch: fetchMyNotes };
}
